import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { AttributionField } from "../shared/AttributionField";
import VendorFormDialog from "../../vendorMaster/VendorFormDialog";
import { money } from "./imprestShared";

const VENDOR_MANAGE_ROLES = ["Director", "Manager", "Back Office"];

// Receipt tallied against an employee's running pool. Site/Equipment is required here.
export default function ImprestBillFormDialog({ refData, employees, employeeId, onClose, onSaved }) {
  useEffect(() => { refData.refresh(["vendors", "sites", "equipment"]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const canManageVendor = VENDOR_MANAGE_ROLES.includes(user?.role);
  const [outstandingBills, setOutstandingBills] = useState([]);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    employee_id: employeeId || "", amount: "", category_id: "", site_id: "", equipment_id: "",
    vendor_code: "", vendor_bill_id: "", bill_amount_applied: "",
    bill_date: new Date().toISOString().slice(0, 10), remarks: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const categories = refData.categories.filter((c) => c.name.trim().toLowerCase() !== "imprest");

  useEffect(() => {
    if (!form.vendor_code) { setOutstandingBills([]); return; }
    client.get("/payment-desk/bills/for-vendor/outstanding", { params: { vendor_code: form.vendor_code } })
      .then((res) => setOutstandingBills(res.data))
      .catch(() => setOutstandingBills([]));
  }, [form.vendor_code]);
  const selectedBill = outstandingBills.find((b) => b.bill_id === form.vendor_bill_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error("Employee is required");
    if (form.amount === "" || Number(form.amount) <= 0) return toast.error("Amount must be greater than 0");
    if (!form.category_id) return toast.error("Category is required");
    if (!form.site_id && !form.equipment_id) return toast.error("Select a Site or Equipment");
    if (!form.bill_date) return toast.error("Date is required");
    if (form.vendor_bill_id && (form.bill_amount_applied === "" || Number(form.bill_amount_applied) <= 0)) {
      return toast.error("Amount Applied to the Bill must be greater than 0");
    }
    const fd = new FormData();
    fd.append("employee_id", form.employee_id);
    fd.append("amount", form.amount);
    fd.append("category_id", form.category_id);
    fd.append("bill_date", form.bill_date);
    if (form.site_id) fd.append("site_id", form.site_id);
    if (form.equipment_id) fd.append("equipment_id", form.equipment_id);
    if (form.vendor_code) fd.append("vendor_code", form.vendor_code);
    if (form.vendor_bill_id) {
      fd.append("vendor_bill_id", form.vendor_bill_id);
      fd.append("bill_amount_applied", form.bill_amount_applied);
    }
    if (form.remarks.trim()) fd.append("remarks", form.remarks.trim());
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      const res = await client.post("/payment-desk/imprest/bills", fd);
      toast.success(`Bill tallied — ${res.data.entry_id}`);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="imprest-bill-dialog">
        <DialogHeader>
          <DialogTitle>Add Imprest Bill</DialogTitle>
          <DialogDescription>Counts against the employee's running imprest balance.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employee *</label>
              <SearchableSelect testId="imprest-bill-employee-select" loading={refData.loadingLists.payeeEmployees} value={form.employee_id} onValueChange={(v) => set("employee_id", v)} placeholder="Select employee"
                options={employees.map((e) => ({ value: e.employee_id, label: `${e.employee_id} — ${e.name}`, testId: `imprest-bill-employee-option-${e.employee_id}` }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount (₹) *</label>
              <Input type="number" step="any" data-testid="imprest-bill-amount-input" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date *</label>
              <Input type="date" data-testid="imprest-bill-date-input" value={form.bill_date} onChange={(e) => set("bill_date", e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Category *</label>
              <SearchableSelect testId="imprest-bill-category-select" loading={refData.loadingLists.categories} value={form.category_id} onValueChange={(v) => set("category_id", v)} placeholder="Select category"
                options={categories.map((c) => ({ value: c.id, label: c.name, testId: `imprest-bill-category-option-${c.id}` }))} />
            </div>
          </div>
          <AttributionField
            siteId={form.site_id} equipmentId={form.equipment_id} sites={refData.sites} equipment={refData.equipment} sitesLoading={refData.loadingLists.sites} equipmentLoading={refData.loadingLists.equipment}
            onChange={({ siteId, equipmentId }) => setForm((f) => ({ ...f, site_id: siteId, equipment_id: equipmentId }))}
            testId="imprest-bill-attribution"
          />
          <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor {form.vendor_bill_id ? "*" : ""}</label>
              <SearchableSelect testId="imprest-bill-vendor-select" loading={refData.loadingLists.vendors} value={form.vendor_code || "none"} placeholder="None"
                onValueChange={(v) => (v === "__add_vendor__" ? setShowAddVendor(true) : setForm((f) => ({ ...f, vendor_code: v === "none" ? "" : v, vendor_bill_id: "", bill_amount_applied: "" })))}
                options={[
                  { value: "none", label: "None", pinned: true },
                  ...refData.vendors.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code, testId: `imprest-bill-vendor-option-${v.vendor_code}` })),
                  ...(canManageVendor ? [{ value: "__add_vendor__", label: "+ Add New Vendor", pinned: true, className: "text-amber-600 font-medium", testId: "imprest-bill-add-new-vendor" }] : []),
                ]} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Linked Vendor Bill (optional)</label>
              <SearchableSelect testId="imprest-bill-vendor-bill-select" value={form.vendor_bill_id || "none"} disabled={!form.vendor_code}
                onValueChange={(v) => setForm((f) => ({ ...f, vendor_bill_id: v === "none" ? "" : v, bill_amount_applied: "" }))}
                placeholder={form.vendor_code ? "None" : "Select a Vendor first"}
                options={[
                  { value: "none", label: "None", pinned: true },
                  ...outstandingBills.map((b) => ({ value: b.bill_id, label: `${b.bill_id} — ${money(b.outstanding_balance)} outstanding`, testId: `imprest-bill-vendor-bill-option-${b.bill_id}` })),
                ]} />
              {selectedBill && (
                <p className="text-xs text-slate-500 mt-1" data-testid="imprest-bill-vendor-bill-balance">
                  Bill {selectedBill.bill_id}: {money(selectedBill.amount)} billed · {money(selectedBill.outstanding_balance)} outstanding
                </p>
              )}
            </div>
            {form.vendor_bill_id && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount Applied to Bill (₹) *</label>
                <Input type="number" step="any" data-testid="imprest-bill-applied-input" value={form.bill_amount_applied} onChange={(e) => set("bill_amount_applied", e.target.value)} />
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bill / Receipt (optional)</label>
            <Input type="file" accept="image/*,application/pdf" data-testid="imprest-bill-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks (optional)</label>
            <Textarea data-testid="imprest-bill-remarks-input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="imprest-bill-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="imprest-bill-submit-button">
              {saving ? "Saving..." : "Add Bill"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      {showAddVendor && (
        <VendorFormDialog
          vendor={null}
          onClose={() => setShowAddVendor(false)}
          onSaved={(newVendor) => {
            setShowAddVendor(false);
            setForm((f) => ({ ...f, vendor_code: newVendor.vendor_code, vendor_bill_id: "", bill_amount_applied: "" }));
          }}
        />
      )}
    </Dialog>
  );
}
